import { http } from '../index';
import { formDataModifier } from '../modifiers';
import { scheduledTrainingSessionsModifier } from '../formatters';
import { downloadFileFromBytes, getFileNameFromContentDispositionString } from '../helpers';

const multiPartConfig = {
  headers: {
    'Content-Type': 'multipart/form-data',
  },
};

export const getTrainingSessionsList = params =>
  http
    .get('/training-sessions/list', { params })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getScheduledTrainingSessions = params =>
  http
    .get('/training-sessions/scheduled', { params })
    .then(res => scheduledTrainingSessionsModifier(res.data))
    .catch(error => error.response?.data || error);

export const getScheduledTrainingSessionsByStation = (stationId, params) =>
  http
    .get(`/station/${stationId}/training-sessions/scheduled`, { params })
    .then(res => scheduledTrainingSessionsModifier(res.data))
    .catch(error => error.response?.data || error);

export const getFlexibleTrainingSessions = params =>
  http
    .get('/training-sessions/scheduled/flexible', { params })
    .then(res => scheduledTrainingSessionsModifier(res.data))
    .catch(error => error.response?.data || error);

export const getFlexibleTrainingSessionsByStation = (stationId, params) =>
  http
    .get(`/station/${stationId}/training-sessions/scheduled/flexible`, { params })
    .then(res => scheduledTrainingSessionsModifier(res.data))
    .catch(error => error.response?.data || error);

export const getTrainingSession = id =>
  http
    .get(`/training-sessions/${id}`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const createTrainingSession = formData =>
  http
    .post('/training-sessions', formData)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const updateTrainingSession = (id, formData) =>
  http
    .put(`/training-sessions/${id}`, formData)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const launchTrainingSession = id =>
  http
    .post(`/training-sessions/${id}/launch`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const cancelTrainingSession = id =>
  http
    .post(`/training-sessions/${id}/cancel`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const deleteTrainingSession = id =>
  http
    .delete(`/training-sessions/${id}`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getTrainingSessionParticipants = (trainingSessionId, params) =>
  http
    .get(`/training-sessions/${trainingSessionId}/participants`, { params })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getTrainingSessionAssignableStudents = trainingSessionId =>
  http
    .get(`/training-sessions/${trainingSessionId}/participants/assignable-students`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const assignTrainingSessionParticipants = (trainingSessionId, data) =>
  http
    .post(`/training-sessions/${trainingSessionId}/participants/assign`, data)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const removeTrainingSessionParticipant = (trainingSessionId, userId) =>
  http
    .delete(`/training-sessions/${trainingSessionId}/participants/${userId}`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const createTrainingSessionEvidence = (trainingSessionId, userId, data = {}) =>
  http
    .post(`/training-sessions/${trainingSessionId}/participants/${userId}/evidence`, data)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const setTrainingSessionParticipantFailed = (trainingSessionId, userId) =>
  http
    .get(`/training-sessions/${trainingSessionId}/participants/${userId}/failed`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const uploadTrainingSessionCertificate = (trainingSessionId, userId, data) =>
  http
    .post(
      `/training-sessions/${trainingSessionId}/participants/${userId}/certificate`,
      formDataModifier(data),
      multiPartConfig,
    )
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const downloadTrainingSessionCertificate = (trainingSessionId, userId) =>
  http
    .get(`/training-sessions/${trainingSessionId}/participants/${userId}/certificate`, {
      responseType: 'blob',
    })
    .then(res => {
      const filename = getFileNameFromContentDispositionString(res);
      downloadFileFromBytes(res.data, filename, '', true);
    })
    .catch(error => error.response?.data || error);

export const deleteTrainingSessionCertificate = (trainingSessionId, userId) =>
  http
    .delete(`/training-sessions/${trainingSessionId}/participants/${userId}/certificate`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const downloadParticipantAssessmentResult = async (trainingSessionId, userId) => {
  try {
    const res = await http.get(
      `/training-sessions/${trainingSessionId}/participants/${userId}/assessment-result/download`,
      {
        responseType: 'blob',
      },
    );
    const filename = getFileNameFromContentDispositionString(res);
    downloadFileFromBytes(res.data, filename, '', true);
    return { error: false };
  } catch (error) {
    const data = error.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        return {
          error: true,
          message: parsed.message || parsed.error || 'assessmentResult.downloadFailed',
        };
      } catch (parseError) {
        return { error: true, message: 'assessmentResult.downloadFailed' };
      }
    }
    return error.response?.data || { error: true, message: 'assessmentResult.downloadFailed' };
  }
};

export const importTrainingSessions = formData =>
  http
    .post('/training-sessions/import', formDataModifier(formData), multiPartConfig)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const exportTrainingSessions = () =>
  http
    .get('/training-sessions/export', { responseType: 'blob' })
    .then(res => {
      const filename = getFileNameFromContentDispositionString(res);
      downloadFileFromBytes(res.data, filename, '', true);
    })
    .catch(error => error.response?.data || error);

export const downloadTrainingSessionsImportExample = () =>
  http
    .get('/training-sessions/import/example', { responseType: 'blob' })
    .then(res => {
      const filename = getFileNameFromContentDispositionString(res);
      downloadFileFromBytes(res.data, filename, '', true);
    })
    .catch(error => error.response?.data || error);

export const getTrainingSessionParticipantAttempts = (trainingSessionId, userId) =>
  http
    .get(`/training-sessions/${trainingSessionId}/participants/${userId}/attempts`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const grantTrainingSessionParticipantRetake = (trainingSessionId, userId) =>
  http
    .post(`/training-sessions/${trainingSessionId}/participants/${userId}/retake`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const downloadAttemptAssessmentResult = async (trainingSessionId, userId, attemptId) => {
  try {
    const res = await http.get(
      `/training-sessions/${trainingSessionId}/participants/${userId}/attempts/${attemptId}/assessment-result/download`,
      {
        responseType: 'blob',
      },
    );
    const filename = getFileNameFromContentDispositionString(res);
    downloadFileFromBytes(res.data, filename, '', true);
    return { error: false };
  } catch (error) {
    const data = error.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        return {
          error: true,
          message: parsed.message || parsed.error || 'assessmentResult.downloadFailed',
        };
      } catch (parseError) {
        return { error: true, message: 'assessmentResult.downloadFailed' };
      }
    }
    return error.response?.data || { error: true, message: 'assessmentResult.downloadFailed' };
  }
};

export const getTrainingSessionFilesList = (trainingSessionId, params) =>
  http
    .get(`/training-sessions/${trainingSessionId}/files/list`, { params })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

const fetchTrainingSessionFileBlob = (trainingSessionId, fileId) =>
  http
    .get(`/training-sessions/${trainingSessionId}/files/${fileId}/download`, {
      responseType: 'blob',
    })
    .then(res => res)
    .catch(error => Promise.reject(error.response?.data || error));

export const openTrainingSessionFile = (trainingSessionId, fileId, fallbackFileName = '') =>
  fetchTrainingSessionFileBlob(trainingSessionId, fileId).then(res => {
    const fileName = getFileNameFromContentDispositionString(res) || fallbackFileName || 'file';
    const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
    const url = window.URL.createObjectURL(blob);
    const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');

    if (!openedWindow) {
      downloadFileFromBytes(res.data, fileName, '', true);
      window.URL.revokeObjectURL(url);
      return res;
    }

    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    return res;
  });

export const downloadTrainingSessionFile = (trainingSessionId, fileId, fallbackFileName = '') =>
  fetchTrainingSessionFileBlob(trainingSessionId, fileId).then(res => {
    const fileName = getFileNameFromContentDispositionString(res) || fallbackFileName || 'file';
    downloadFileFromBytes(res.data, fileName, '', true);
    return res;
  });

export const setTrainingSessionRating = (trainingSessionId, data) =>
  http
    .post(`/training-session/${trainingSessionId}/rating`, data)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getTrainingSessionRating = trainingSessionId =>
  http
    .get(`/training-session/${trainingSessionId}/rating`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getTrainerTrainingSessionAvgRatings = userId =>
  http
    .get(`/trainer/${userId}/training-sessions/avg-ratings`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getStudentRequiredTrainingSessions = (userId, params) =>
  http
    .get(`/students/${userId}/required-training-sessions`, { params })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const applyStudentRequiredTrainingSessions = (userId, rows) =>
  http
    .post(`/students/${userId}/required-training-sessions/apply`, { rows })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const previewStudentJobRoleChange = (userId, jobsTasksIds) =>
  http
    .post(`/students/${userId}/job-roles/change/preview`, { jobsTasksIds })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const applyStudentJobRoleChange = (
  userId,
  jobsTasksIds,
  keepEnrolledSessionIds = [],
  assignSessionRows = [],
) =>
  http
    .post(`/students/${userId}/job-roles/change/apply`, {
      jobsTasksIds,
      keepEnrolledSessionIds,
      assignSessionRows,
    })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getStudentTrainingSessionsInProgress = (userId, params) =>
  http
    .get(`/student/${userId}/training-sessions/in-progress`, { params })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getStudentTrainingSessionsScheduled = (userId, params) =>
  http
    .get(`/student/${userId}/training-sessions/scheduled`, { params })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const getStudentTrainingSessionsPassed = (userId, params) =>
  http
    .get(`/student/${userId}/training-sessions/passed`, { params })
    .then(res => {
      const data = res.data;
      const items = (data.items || []).map(item => ({
        ...item,
        buttonParams: {
          id: item.id,
          certificate: item.certificatePreviewUrl
            ? { previewUrl: item.certificatePreviewUrl }
            : item.hasCertificate
            ? { id: item.evidenceId }
            : null,
          isPerformedWithSuccess: item.participantStatus === 'completed',
          isMoodleCourse: !!item.isMoodleSession,
          status:
            item.participantStatus === 'completed'
              ? 'Passed'
              : item.participantStatus === 'failed'
              ? 'Failed'
              : item.participantStatus,
          isFrozen: false,
        },
      }));
      return { ...data, items };
    })
    .catch(error => error.response?.data || error);

export const getStudentEvidences = (userId, params) =>
  http
    .get(`/student/${userId}/evidences`, { params })
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const createStudentExternalEvidence = (userId, data) =>
  http
    .post(`/student/${userId}/evidences`, data)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const updateStudentExternalEvidence = (userId, evidenceId, data) =>
  http
    .patch(`/student/${userId}/evidences/${evidenceId}`, data)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const deleteStudentExternalEvidence = (userId, evidenceId) =>
  http
    .delete(`/student/${userId}/evidences/${evidenceId}`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const uploadStudentEvidenceCertificate = (userId, evidenceId, data) =>
  http
    .post(`/student/${userId}/evidences/${evidenceId}/certificate`, formDataModifier(data), multiPartConfig)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const downloadStudentEvidenceCertificate = (userId, evidenceId) =>
  http
    .get(`/student/${userId}/evidences/${evidenceId}/certificate`, {
      responseType: 'blob',
    })
    .then(res => {
      const filename = getFileNameFromContentDispositionString(res);
      downloadFileFromBytes(res.data, filename, '', true);
    })
    .catch(error => error.response?.data || error);

export const deleteStudentEvidenceCertificate = (userId, evidenceId) =>
  http
    .delete(`/student/${userId}/evidences/${evidenceId}/certificate`)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const importStudentEvidences = (userId, data) =>
  http
    .post(`/student/${userId}/evidences/import`, formDataModifier(data), multiPartConfig)
    .then(res => res.data)
    .catch(error => error.response?.data || error);

export const downloadStudentEvidencesExample = userId =>
  http
    .get(`/student/${userId}/evidences/import/example`, {
      responseType: 'blob',
    })
    .then(res => {
      const filename = getFileNameFromContentDispositionString(res);
      downloadFileFromBytes(res.data, filename, '', true);
    })
    .catch(error => error.response?.data || error);
